// NotifiedBadge — compact "notification sent" indicator for list rows.
//
// Mirrors the "Sent · HH:MM" chip pattern from RemindersTab but condensed
// into a single inline badge so it fits in a table cell. Drives off
// generic emailSent / smsSent booleans (with optional sent-at timestamps
// for the tooltip) so the same component works for booking reminders
// and receipt sends.

import { Mail, MessageSquare, BellOff } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Tooltip } from '../Tooltip/Tooltip';
import styles from './NotifiedBadge.module.css';

export interface NotifiedBadgeProps {
  emailSent: boolean;
  smsSent: boolean;
  emailSentAt?: string | null;
  smsSentAt?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export function NotifiedBadge({
  emailSent,
  smsSent,
  emailSentAt,
  smsSentAt,
  size = 'sm',
  className,
}: NotifiedBadgeProps): JSX.Element {
  const iconSize = size === 'md' ? 14 : 12;

  const emailTime = formatTime(emailSentAt);
  const smsTime = formatTime(smsSentAt);

  let label: string;
  let icons: JSX.Element;
  let variantClass: string;

  if (emailSent && smsSent) {
    label = 'Notified';
    icons = (
      <>
        <Mail size={iconSize} aria-hidden="true" />
        <MessageSquare size={iconSize} aria-hidden="true" />
      </>
    );
    variantClass = styles.sent ?? '';
  } else if (emailSent) {
    label = 'Email sent';
    icons = <Mail size={iconSize} aria-hidden="true" />;
    variantClass = styles.sent ?? '';
  } else if (smsSent) {
    label = 'SMS sent';
    icons = <MessageSquare size={iconSize} aria-hidden="true" />;
    variantClass = styles.sent ?? '';
  } else {
    label = 'Not notified';
    icons = <BellOff size={iconSize} aria-hidden="true" />;
    variantClass = styles.muted ?? '';
  }

  const tooltipParts: string[] = [];
  if (emailSent && emailTime) tooltipParts.push(`Email: ${emailTime}`);
  if (smsSent && smsTime) tooltipParts.push(`SMS: ${smsTime}`);
  const tooltip = tooltipParts.length > 0 ? tooltipParts.join(' · ') : null;

  const badge = (
    <span
      className={cn(
        styles.badge,
        variantClass,
        size === 'md' ? styles.md : styles.sm,
        className,
      )}
      data-notified={emailSent || smsSent ? 'true' : 'false'}
    >
      <span className={styles.icons} aria-hidden="true">
        {icons}
      </span>
      <span className={styles.label}>{label}</span>
    </span>
  );

  if (tooltip === null) {
    return badge;
  }

  return <Tooltip content={tooltip}>{badge}</Tooltip>;
}
